"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosPaymentTransactionSchema = exports.APosPaymentAmountsSchema = exports.APosGatewayProviderSchema = exports.APosNormalisedMethodSchema = exports.APosChargebackStatusSchema = exports.APosSettlementStatusSchema = exports.APosPaymentTransactionStatusSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
exports.APosPaymentTransactionStatusSchema = zod_1.z.enum([
    'pending', 'authorized', 'captured', 'settled', 'failed',
    'cancelled', 'refunded', 'partially_refunded',
    'sent_to_poi', 'completed', 'declined', 'voided', 'timeout', 'orphaned',
    'disputed', // chargeback received, awaiting response window
    'chargeback_lost', 'chargeback_won',
]);
exports.APosSettlementStatusSchema = zod_1.z.enum(['pending', 'settled', 'failed']);
exports.APosChargebackStatusSchema = zod_1.z.enum(['none', 'disputed', 'lost', 'won', 'withdrawn']);
exports.APosNormalisedMethodSchema = zod_1.z.enum(['cash', 'card', 'ebt']);
exports.APosGatewayProviderSchema = zod_1.z.enum([
    'manual_external', 'nmi_cloud', 'nmi_direct', 'nmi_direct_vp3350', 'dejavoo_spin_usb', 'cash', 'stripe',
    'sunbay_nexus',
]);
exports.APosPaymentAmountsSchema = zod_1.z.object({
    subtotal: zod_1.z.number().nonnegative().default(0),
    /** The leg's tax (pos-payments process-payment, Task 21). Older rows have none → 0. */
    tax: zod_1.z.number().nonnegative().default(0),
    tip: zod_1.z.number().nonnegative().default(0),
    surcharge: zod_1.z.number().nonnegative().default(0),
    surchargeRate: zod_1.z.number().nonnegative().default(0),
    surchargeFlat: zod_1.z.number().nonnegative().default(0),
    total: zod_1.z.number().nonnegative().default(0),
});
exports.APosPaymentTransactionSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    paymentTransactionId: id_schema_1.EntityIdSchema,
    locationId: id_schema_1.EntityIdSchema,
    orderId: id_schema_1.EntityIdSchema.optional(),
    transactionId: id_schema_1.EntityIdSchema.optional(),
    registerId: zod_1.z.string().optional(),
    employeeId: zod_1.z.string().optional(),
    batchId: zod_1.z.string().optional(),
    gateway: zod_1.z.string().min(1),
    gatewayTransactionId: zod_1.z.string().optional(),
    // Dejavoo SPIn linked-refund references — persisted on a credit_dejavoo sale so
    // a later Return can be keyed to the ORIGINAL transaction/card (RRN is SPIn's
    // primary linked key; gatewayRefId is the original SPIn RefId). Additive.
    gatewayRrn: zod_1.z.string().optional(),
    gatewayRefId: zod_1.z.string().optional(),
    method: zod_1.z.enum(['card', 'contactless', 'apple_pay', 'google_pay', 'cash', 'store_credit']),
    // Normalised bucket used by reports — collapses card_emv/external_card/card → 'card'
    normalisedMethod: exports.APosNormalisedMethodSchema.optional(),
    // Gateway provider used — for internal sub-tracking in detail views
    gatewayProvider: exports.APosGatewayProviderSchema.optional(),
    /**
     * WHAT THE CUSTOMER SAID THE CARD WAS — the evidence behind the surcharge.
     *
     * A surcharge may lawfully be added to a credit transaction and not to a
     * debit one, and nothing in the stack can tell them apart: the readers
     * report brand and last four, never the product type. So the customer is
     * asked, and their answer decides whether a fee is charged.
     *
     * That makes this the ONLY proof of why a given sale was surcharged, which
     * is why it is recorded on the payment rather than inferred later from the
     * presence of a fee. It is a DECLARATION, not an observation — named so
     * nobody reads it as something the processor confirmed.
     *
     * Absent on every payment taken before the question existed, and on any
     * tender that is never asked (cash, EBT), so reports must treat it as
     * unknown rather than as a denial.
     */
    customerSelectedCardType: zod_1.z.enum(['credit', 'debit']).optional(),
    amount: zod_1.z.number().nonnegative(),
    currency: zod_1.z.string().length(3).default('USD'),
    tipAmount: zod_1.z.number().nonnegative().default(0),
    processingFee: zod_1.z.number().nonnegative().default(0),
    netAmount: zod_1.z.number(),
    status: exports.APosPaymentTransactionStatusSchema.default('pending'),
    cardBrand: zod_1.z.string().optional(),
    cardLast4: zod_1.z.string().length(4).optional(),
    cardEntryMethod: zod_1.z.enum(['chip', 'swipe', 'contactless', 'manual', 'online']).optional(),
    emvData: zod_1.z.string().optional(),
    emvDeviceId: zod_1.z.string().nullable().optional(),
    authorizationCode: zod_1.z.string().optional(),
    approvalCode: zod_1.z.string().optional(),
    referenceNumber: zod_1.z.string().optional(),
    refundedAmount: zod_1.z.number().nonnegative().default(0),
    refundTransactionIds: zod_1.z.array(zod_1.z.string()).default([]),
    failureReason: zod_1.z.string().optional(),
    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
    // Additive NMI fields
    amounts: exports.APosPaymentAmountsSchema.optional(),
    nmiResponse: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable().optional(),
    webhookReceivedAt: zod_1.z.string().nullable().optional(),
    idempotencyKey: zod_1.z.string().nullable().optional(),
    processedAt: zod_1.z.number().optional(),
    settledAt: zod_1.z.number().optional(),
    // --- Settlement (populated by settlement.batch.* NMI webhook) ---
    settlementStatus: exports.APosSettlementStatusSchema.optional(),
    settlementBatchId: zod_1.z.string().optional(),
    // --- Chargeback (populated by chargeback.batch.complete NMI webhook) ---
    chargebackStatus: exports.APosChargebackStatusSchema.default('none').optional(),
    chargebackAt: zod_1.z.number().optional(),
    chargebackAmount: zod_1.z.number().nonnegative().optional(),
    chargebackReason: zod_1.z.string().optional(),
    chargebackCaseNumber: zod_1.z.string().optional(),
}).merge(timestamps_schema_1.TimestampsSchema);
//# sourceMappingURL=payment-transaction.schema.js.map